import { useEffect } from 'react'

/**
 * useSEO — Dynamically injects per-page SEO metadata into <head>.
 *
 * Handles:
 *  - document.title
 *  - <meta name="description">
 *  - <meta name="keywords">
 *  - <link rel="canonical">
 *  - Open Graph tags (og:title, og:description, og:url, og:image, og:type, og:site_name)
 *  - Twitter Card tags
 *  - JSON-LD structured data (Organization / AboutPage / ContactPage / Person)
 *
 * @param {Object} options
 * @param {string} options.title           - Page <title>
 * @param {string} options.description     - Meta description (≤160 chars)
 * @param {string} [options.keywords]      - Comma-separated keywords
 * @param {string} [options.canonical]     - Canonical URL for this page
 * @param {string} [options.ogImage]       - Absolute URL to OG image (1200×630)
 * @param {string} [options.ogType]        - OG type — defaults to 'website'
 * @param {Object|Object[]} [options.jsonLd] - JSON-LD structured data object(s)
 */
export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://augmentappz.ai/og-image.png',
  ogType = 'website',
  jsonLd,
} = {}) {
  useEffect(() => {
    /* ── Title ─────────────────────────────────────────────────────── */
    if (title) document.title = title

    /* ── Helper: upsert a <meta> tag ───────────────────────────────── */
    const setMeta = (attr, key, value) => {
      if (!value) return
      let el = document.querySelector(`meta[${attr}="${key}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', value)
    }

    /* ── Helper: upsert a <link> tag ───────────────────────────────── */
    const setLink = (rel, value) => {
      if (!value) return
      let el = document.querySelector(`link[rel="${rel}"]`)
      if (!el) {
        el = document.createElement('link')
        el.setAttribute('rel', rel)
        document.head.appendChild(el)
      }
      el.setAttribute('href', value)
    }

    /* ── Standard meta ─────────────────────────────────────────────── */
    setMeta('name', 'description', description)
    setMeta('name', 'keywords', keywords)

    /* ── Canonical ─────────────────────────────────────────────────── */
    setLink('canonical', canonical)

    /* ── Open Graph ────────────────────────────────────────────────── */
    setMeta('property', 'og:type',        ogType)
    setMeta('property', 'og:site_name',   'AugmentAppz')
    setMeta('property', 'og:title',       title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url',         canonical)
    setMeta('property', 'og:image',       ogImage)
    setMeta('property', 'og:image:width',  '1200')
    setMeta('property', 'og:image:height', '630')
    setMeta('property', 'og:image:alt',    title || 'AugmentAppz')
    setMeta('property', 'og:locale',      'en_US')

    /* ── Twitter Card ──────────────────────────────────────────────── */
    setMeta('name', 'twitter:card',        'summary_large_image')
    setMeta('name', 'twitter:site',        '@AugmentWithBala')
    setMeta('name', 'twitter:title',       title)
    setMeta('name', 'twitter:description', description)
    setMeta('name', 'twitter:image',       ogImage)

    /* ── JSON-LD Structured Data ───────────────────────────────────── */
    const SCRIPT_ID = 'seo-jsonld'
    if (jsonLd) {
      let script = document.getElementById(SCRIPT_ID)
      if (!script) {
        script = document.createElement('script')
        script.id = SCRIPT_ID
        script.type = 'application/ld+json'
        document.head.appendChild(script)
      }
      const payload = Array.isArray(jsonLd)
        ? { '@context': 'https://schema.org', '@graph': jsonLd }
        : jsonLd
      script.textContent = JSON.stringify(payload, null, 2)
    }

    /* ── Cleanup: reset title on unmount ───────────────────────────── */
    return () => {
      document.title = 'AugmentAppz — AI-Native Enterprise Infrastructure'
    }
  }, [title, description, keywords, canonical, ogImage, ogType, jsonLd])
}
